import { useState, useEffect, useRef, type KeyboardEvent } from "react";
import { Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { marked } from "marked";

interface AskAIProps {
  selectedChatId?: string;
  onScrollToDate?: (date: string) => void;
}

export function AskAI({ selectedChatId, onScrollToDate }: AskAIProps) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const answerRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async () => {
    if (!selectedChatId || !question.trim() || isLoading) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/chats/${selectedChatId}/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: question.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        setAnswer(data.answer);
      } else {
        setAnswer("Ошибка при получении ответа. Попробуйте еще раз.");
      }
    } catch (error) {
      console.error("Error asking question:", error);
      setAnswer("Ошибка при получении ответа. Попробуйте еще раз.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const processAnswerToHTML = (markdown: string): string => {
    const html = marked(markdown) as string;

    const dateRegex = /\b(\d{4}-\d{2}-\d{2})\b/g;
    const processedHTML = html.replace(dateRegex, (match) => {
      return `<span class="date-link" data-date="${match}">${match}</span>`;
    });

    return processedHTML;
  };

  useEffect(() => {
    if (answerRef.current) {
      const dateLinks = answerRef.current.querySelectorAll('.date-link');

      const handleDateClick = (e: Event) => {
        const target = e.currentTarget as HTMLElement;
        const date = target.getAttribute('data-date');
        if (date && onScrollToDate) {
          onScrollToDate(date);
        }
      };

      dateLinks.forEach(link => {
        link.addEventListener('click', handleDateClick);
      });

      return () => {
        dateLinks.forEach(link => {
          link.removeEventListener('click', handleDateClick);
        });
      };
    }
  }, [answer, onScrollToDate]);

  if (!selectedChatId) {
    return (
      <div className="bg-muted rounded-lg p-6 h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Выберите чат, чтобы задать вопрос</p>
      </div>
    );
  }

  return (
    <div className="bg-muted rounded-lg h-full overflow-hidden flex flex-col">
      <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
        <h2 className="text-lg font-semibold">Ask AI</h2>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Получаю ответ...
          </div>
        ) : answer ? (
          <div
            ref={answerRef}
            className="text-sm text-foreground leading-relaxed markdown-content"
            dangerouslySetInnerHTML={{ __html: processAnswerToHTML(answer) }}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Задайте вопрос о чате, и AI ответит на него, анализируя историю сообщений.
          </p>
        )}
      </div>

      <div className="p-6 pt-4 flex-shrink-0 border-t border-border">
        <div className="flex gap-2">
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Задайте вопрос о чате..."
            rows={3}
            disabled={isLoading}
            className="resize-none"
          />
          <Button
            onClick={handleSubmit}
            disabled={!question.trim() || isLoading}
            size="sm"
            className="self-end"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
